import { memo, useCallback, useEffect, useState } from "react";
import { transformDateToLocalDateString } from "helpers";
import { InvoiceInternalNote } from "services/api/fleet/vehicles/invoices/types";
import {
    getInvoiceInternalNotesAPI,
    createInvoiceInternalNoteAPI,
    deleteInvoiceInternalNoteAPI,
} from "services/api/fleet/vehicles/invoices/api";
import { Button } from "ui/components/Button/Button";
import { TextArea } from "ui/components/TextField/TextArea";
import { ReactSVG } from "react-svg";

interface InvoiceInternalNotesProps {
    invoiceId?: string;
}

const InvoiceInternalNotes: React.FC<InvoiceInternalNotesProps> = ({ invoiceId }) => {
    const [notes, setNotes] = useState<InvoiceInternalNote[]>([]);
    const [newNoteContent, setNewNoteContent] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    const loadNotes = useCallback(async () => {
        if (!invoiceId) return;
        setIsLoading(true);
        try {
            const result = await getInvoiceInternalNotesAPI(invoiceId);
            setNotes(result);
        } catch { }
        setIsLoading(false);
    }, [invoiceId]);

    useEffect(() => {
        loadNotes();
    }, [loadNotes]);

    const handleAddNote = async () => {
        if (!invoiceId || !newNoteContent.trim()) return;

        try {
            const note = await createInvoiceInternalNoteAPI(invoiceId, newNoteContent.trim());
            setNotes(prev => [note, ...prev]);
            setNewNoteContent("");
        } catch { }
    };

    const handleDeleteNote = async (noteId: string) => {
        if (!invoiceId) return;

        try {
            await deleteInvoiceInternalNoteAPI(invoiceId, noteId);
            setNotes(prev => prev.filter(n => n.id !== noteId));
        } catch { }
    };

    if (!invoiceId) {
        return (
            <p className="text-sm text-slate-500 p-4">
                Spremite račun da biste mogli dodavati interne napomene.
            </p>
        );
    }

    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
                <TextArea
                    name="newNote"
                    placeholder="Unesite internu napomenu..."
                    value={newNoteContent}
                    onChange={(e: any) => setNewNoteContent(e.target.value)}
                />
                <div className="flex justify-end">
                    <Button
                        variant="primary"
                        children="Dodaj napomenu"
                        onClick={handleAddNote}
                        startIcon="/media/icons/plus.svg"
                    />
                </div>
            </div>

            {notes.length > 0 && (
                <div className="flex flex-col gap-2">
                    {notes.map((note) => (
                        <div
                            key={note.id}
                            className="flex items-start gap-3 p-3 bg-slate-50 border border-slate-200 rounded"
                        >
                            <div className="flex-1">
                                <p className="text-sm whitespace-pre-wrap">{note.content}</p>
                                <div className="flex gap-2 mt-2 text-xs text-slate-400">
                                    <span>{note.userCreated?.firstName} {note.userCreated?.lastName}</span>
                                    <span>&middot;</span>
                                    <span>{transformDateToLocalDateString(note.created ? new Date(note.created) : null)}</span>
                                </div>
                            </div>
                            <span
                                onClick={() => handleDeleteNote(note.id)}
                                className="flex items-center justify-center w-8 h-8 cursor-pointer bg-red-50 hover:bg-red-100 rounded-full shrink-0"
                            >
                                <ReactSVG
                                    src="/media/icons/trash.svg"
                                    className="w-3.5 h-3.5 stroke-[#bd0000]"
                                />
                            </span>
                        </div>
                    ))}
                </div>
            )}

            {!isLoading && notes.length === 0 && (
                <p className="text-sm text-slate-400">Nema internih napomena.</p>
            )}
        </div>
    );
};

export default memo(InvoiceInternalNotes);
